const DEFAULT_TTL_MS = 60_000;

type CacheEntry<T> = {
  value: T;
  at: number;
};

const stores = new Map<string, Map<string, CacheEntry<unknown>>>();

function storeFor(namespace: string): Map<string, CacheEntry<unknown>> {
  let store = stores.get(namespace);
  if (!store) {
    store = new Map();
    stores.set(namespace, store);
  }
  return store;
}

export function readSuiteFolderListCache<T>(namespace: string, key: string, ttlMs = DEFAULT_TTL_MS): T | null {
  const hit = storeFor(namespace).get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > ttlMs) {
    storeFor(namespace).delete(key);
    return null;
  }
  return hit.value as T;
}

export function writeSuiteFolderListCache<T>(namespace: string, key: string, value: T): void {
  storeFor(namespace).set(key, { value, at: Date.now() });
}

export function dropSuiteFolderListCache(namespace: string, key: string): void {
  storeFor(namespace).delete(key);
}

export function prefetchSuiteFolderList<T>(
  namespace: string,
  key: string,
  loader: () => Promise<T>,
  ttlMs = DEFAULT_TTL_MS
): void {
  if (readSuiteFolderListCache<T>(namespace, key, ttlMs) != null) return;
  void loader().then((value) => {
    writeSuiteFolderListCache(namespace, key, value);
  });
}

export async function loadSuiteFolderListCached<T>(
  namespace: string,
  key: string,
  loader: () => Promise<T>,
  options: { ttlMs?: number; signal?: AbortSignal } = {}
): Promise<T> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const cached = readSuiteFolderListCache<T>(namespace, key, ttlMs);
  if (cached != null) return cached;

  if (options.signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const value = await loader();
  writeSuiteFolderListCache(namespace, key, value);
  return value;
}
